import React, { useState, useEffect } from 'react';
import { Phone, Menu, X, Globe } from 'lucide-react';
import { businessInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      if (window.__lenis) {
        window.__lenis.scrollTo(element, { offset: -80, duration: 1.4 });
      } else {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md border-b border-gray-200' : 'bg-white/98 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <div className="flex items-center">
              <img 
                src="/images/logo/logo-icon.png" 
                alt="Colombian Body Contour Spa" 
                className="h-16 w-auto object-contain"
              />
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('about')}
              className="nav-link text-sm font-normal text-black hover:text-gray-600 transition-colors duration-200"
            >
              {t.nav.about}
            </button>
            <button 
              onClick={() => scrollToSection('services')}
              className="nav-link text-sm font-normal text-black hover:text-gray-600 transition-colors duration-200"
            >
              {t.nav.services}
            </button>
            <button 
              onClick={() => scrollToSection('why-us')}
              className="nav-link text-sm font-normal text-black hover:text-gray-600 transition-colors duration-200"
            >
              {t.nav.whyUs}
            </button>
            <button 
              onClick={() => scrollToSection('reviews')}
              className="nav-link text-sm font-normal text-black hover:text-gray-600 transition-colors duration-200"
            >
              {t.nav.reviews}
            </button>
            <button 
              onClick={() => scrollToSection('gallery')}
              className="nav-link text-sm font-normal text-black hover:text-gray-600 transition-colors duration-200"
            >
              {t.nav.gallery}
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="nav-link text-sm font-normal text-black hover:text-gray-600 transition-colors duration-200"
            >
              {t.nav.contact}
            </button>
          </nav>

          {/* Right side actions */}
          <div className="hidden lg:flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-2 text-sm text-black hover:text-gray-600 transition-colors duration-200"
              aria-label="Toggle language"
            >
              <Globe size={18} />
              <span className="font-normal">{language === 'en' ? 'ES' : 'EN'}</span>
            </button>
            
            <a 
              href={`tel:${businessInfo.phone}`}
              className="flex items-center space-x-2 bg-transparent border-2 border-black text-black px-6 py-3 text-sm font-medium hover:bg-black hover:text-white transition-all duration-200"
              style={{ borderRadius: '0px' }}
            >
              <Phone size={16} />
              <span>{businessInfo.phone}</span>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-black"
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-3">
            <button 
              onClick={() => scrollToSection('about')}
              className="block w-full text-left text-sm font-normal text-gray-900 py-2"
            >
              {t.nav.about}
            </button>
            <button 
              onClick={() => scrollToSection('services')}
              className="block w-full text-left text-sm font-normal text-gray-900 py-2"
            >
              {t.nav.services}
            </button>
            <button 
              onClick={() => scrollToSection('why-us')}
              className="block w-full text-left text-sm font-normal text-gray-900 py-2"
            >
              {t.nav.whyUs}
            </button>
            <button 
              onClick={() => scrollToSection('reviews')}
              className="block w-full text-left text-sm font-normal text-gray-900 py-2"
            >
              {t.nav.reviews}
            </button>
            <button 
              onClick={() => scrollToSection('gallery')}
              className="block w-full text-left text-sm font-normal text-gray-900 py-2"
            >
              {t.nav.gallery}
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left text-sm font-normal text-gray-900 py-2"
            >
              {t.nav.contact}
            </button>
            
            <div className="pt-4 border-t border-gray-200 space-y-3">
              <button
                onClick={toggleLanguage}
                className="flex items-center space-x-2 text-sm text-gray-900"
              >
                <Globe size={18} />
                <span>{language === 'en' ? 'Español' : 'English'}</span>
              </button>
              
              <a 
                href={`tel:${businessInfo.phone}`}
                className="flex items-center justify-center space-x-2 bg-transparent border-2 border-black text-black px-6 py-3 text-sm font-medium hover:bg-black hover:text-white transition-all duration-200 w-full"
                style={{ borderRadius: '0px' }}
              >
                <Phone size={16} />
                <span>{businessInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
